/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

const CATEGORY_CONFIG = {
  DOCTOR:  { icon: LocalHospitalIcon, bg: '#f3e5f5', border: '#ce93d8', icon_color: '#6a1b9a', label_color: '#4a148c', dot: '#9c27b0' },
  PROFILE: { icon: PersonIcon,        bg: '#e3f2fd', border: '#90caf9', icon_color: '#1565c0', label_color: '#0d47a1', dot: '#2196f3' },
  PHONE:   { icon: PhoneIcon,         bg: '#e0f2f1', border: '#80cbc4', icon_color: '#004d40', label_color: '#004d40', dot: '#009688' },
  ADDRESS: { icon: HomeIcon,          bg: '#fff8e1', border: '#ffe082', icon_color: '#f57f17', label_color: '#e65100', dot: '#ffc107' },
  CREATED: { icon: AddCircleOutlineIcon, bg: '#e8f5e9', border: '#a5d6a7', icon_color: '#2e7d32', label_color: '#1b5e20', dot: '#4caf50' },
  DELETED: { icon: CancelIcon,        bg: '#ffebee', border: '#ef9a9a', icon_color: '#b71c1c', label_color: '#b71c1c', dot: '#f44336' },
  UPDATED: { icon: EditIcon,          bg: '#e8eaf6', border: '#9fa8da', icon_color: '#283593', label_color: '#1a237e', dot: '#3f51b5' },
};

const TABLE_LABELS = {
  irm_contacts: 'Patient Profile',
  irm_phones: 'Phone Number',
  irm_addresses: 'Address',
  irm_contact_doctor_map: 'Assigned Doctor',
  irm_couples: 'Couple / Spouse Link',
  irm_documents: 'Patient Document',
  irm_contact_histories: 'Patient History',
  irm_contact_obsteric_histories: 'Obstetric History',
  irm_contact_miscarriage_histories: 'Miscarriage History',
  irm_ivf_procedure_lab_performance_records: 'IVF Lab Performance',
  irm_appointments: 'OPD Appointment',
  irm_procedures: 'IVF Procedure',
  irm_procedure_folliculometries: 'Folliculometry',
  irm_procedure_folliculometry_days: 'Folliculometry Day',
  irm_procedure_folliculometry_day_records: 'Folliculometry Follicle Records',
  irm_procedure_folliculometry_day_params: 'Folliculometry Day Params',
  irm_procedure_folliculometry_day_drugs: 'Folliculometry Day Drugs',
  irm_procedure_oocyte_collections: 'Oocyte Collection',
  irm_procedure_embro_transfers: 'Embryo Transfer',
  irm_procedure_embro_transfer_details: 'Embryo Transfer Details',
  irm_procedure_embros_transferred: 'Transferred Embryos',
  irm_procedure_embryo_outcomes: 'Embryo Outcome',
  irm_procedure_embryo_outcome_day_records: 'Embryo Outcome Day Records',
  irm_procedure_embryo_outcome_usg_records: 'Embryo Outcome USG Records',
  irm_procedure_embryo_final_outcomes: 'Embryo Final Outcome',
  irm_procedure_embryo_fet_monitoring: 'FET Monitoring',
  irm_procedure_embryo_fet_monitoring_records: 'FET Monitoring Records',
  irm_procedure_embryo_fet_monitoring_endometrial_acceptabilities: 'FET Endometrial Acceptability',
  irm_procedure_donors: 'IVF Donor',
  irm_procedure_histories: 'IVF Procedure History',
  irm_procedure_usgs: 'IVF Procedure USG',
};

const HISTORY_TABLES = [
  'irm_contact_histories',
  'irm_contact_obsteric_histories',
  'irm_contact_miscarriage_histories',
];

const humanizeField = (field) => {
  if (!field) return '';
  // "..._details" fields hold a structured JSON blob (CAN/CANE/Straw entry,
  // Lab Condition reading, etc.) — the raw indicator key + "_details" suffix
  // reads as machine-ish (e.g. "Cryo Vitri D5 Straw Entry Details"), so trim
  // it to a plain "— Details" tag instead.
  const isDetails = field.endsWith('_details');
  const base = isDetails ? field.slice(0, -8) : field;
  const label = base
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return isDetails ? `${label} — Details` : label;
};

// camelCase/snake_case JSON object keys (e.g. from a `target` blob:
// rescuedDay, embryo_count) → "Rescued Day", "Embryo Count".
const humanizeKey = (key) => String(key)
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const looksLikeDateString = (s) => /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2})?/.test(s);

const fmtInlineDateTime = (s) => {
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  const hasTime = s.includes(':');
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(hasTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

// Renders one JSON primitive/nested value as plain text — booleans as
// Yes/No, date-looking strings formatted, nested objects/arrays flattened
// one level so nothing ever falls back to raw "{...}" syntax.
const formatJsonPrimitive = (val) => {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      const items = val.map(formatJsonPrimitive).filter((x) => x !== null);
      return items.length ? items.join(', ') : null;
    }
    const parts = Object.entries(val)
      .map(([k, v]) => {
        const fv = formatJsonPrimitive(v);
        return fv === null ? null : `${humanizeKey(k)}: ${fv}`;
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  const s = String(val);
  return looksLikeDateString(s) ? fmtInlineDateTime(s) : s;
};

// The backend audit-log writer (audit-log.repository.mysql.ts) always
// applies exactly one JSON.stringify on top of whatever old_value/new_value
// it receives. Most callers pass the raw value (one JSON.parse recovers
// it), but two things stack an extra layer: (a) a couple of writers
// pre-stringify their values before sending (double-encoding every value,
// including plain scalars — they end up showing with visible quote marks),
// and (b) fields that store a JSON blob as their actual value (straw/CAN/
// CANE target, Lab Condition reading, outcome record) are themselves
// already a JSON string before the repo's layer is added. Both cases look
// the same from here: "the parsed result is still a string that itself
// looks like more JSON". Keep unwrapping until it stops, so every case
// recovers the true value regardless of how many layers were stacked
// upstream — bounded so a value that legitimately starts with `{`/`[`/`"`
// but isn't real JSON just falls out unchanged instead of looping.
const fullyUnwrap = (raw, maxDepth = 4) => {
  let current = raw;
  for (let i = 0; i < maxDepth; i++) {
    if (typeof current !== 'string') return current;
    const t = current.trim();
    if (!t) return current;
    const looksJsonish = /^[{["]/.test(t) || t === 'true' || t === 'false' || t === 'null';
    if (!looksJsonish) return current;
    let next;
    try {
      next = JSON.parse(t);
    } catch {
      return current;
    }
    if (next === current) return next;
    current = next;
  }
  return current;
};

// Parses a raw old_value/new_value string that's actually a JSON object or
// array (straw/CAN/CANE target blobs, lab condition readings, outcome
// records, etc.) into ordered {label, value} pairs for structured
// rendering — returns null when the value isn't a JSON object/array so the
// caller falls back to plain-text display.
const parseJsonEntries = (raw) => {
  const parsed = fullyUnwrap(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return null;
    return parsed.map((item, i) => ({ label: `#${i + 1}`, value: formatJsonPrimitive(item) ?? '—' }));
  }
  const entries = Object.entries(parsed)
    .map(([k, v]) => ({ label: humanizeKey(k), value: formatJsonPrimitive(v) }))
    .filter((e) => e.value !== null);
  return entries.length ? entries : null;
};

const parseJsonObject = (raw) => {
  const parsed = fullyUnwrap(raw);
  return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
};

// For an UPDATE on a JSON object field, diffs old vs new key-by-key so the
// trail shows only what actually changed ("Rescued Count: 2 → 3") instead
// of two full snapshots the reader has to compare by eye — most updates to
// a straw/CAN/CANE/lab-condition blob only touch one or two keys, so
// dumping the whole object twice buried the actual change.
const diffJsonEntries = (oldRaw, newRaw) => {
  const oldObj = parseJsonObject(oldRaw);
  const newObj = parseJsonObject(newRaw);
  if (!oldObj && !newObj) return null;
  const keys = [...new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})])];
  const rows = keys
    .map((k) => {
      const oldValue = formatJsonPrimitive(oldObj?.[k]);
      const newValue = formatJsonPrimitive(newObj?.[k]);
      if (oldValue === newValue) return null;
      return { label: humanizeKey(k), oldValue: oldValue ?? '—', newValue: newValue ?? '—' };
    })
    .filter(Boolean);
  return rows.length ? rows : null;
};

const safeDisplayValue = (raw) => {
  if (raw === null || raw === undefined) return 'Not set';
  const parsed = fullyUnwrap(raw);
  if (parsed === null || parsed === undefined || parsed === '') return 'Not set';
  if (typeof parsed === 'object') {
    const entries = parseJsonEntries(raw);
    return entries ? entries.map((e) => `${e.label}: ${e.value}`).join(', ') : 'Not set';
  }
  return String(parsed);
};

// Rich-text fields (notes, remarks, "Other" fields, etc.) are stored as HTML
// from the editor — detect that so the timeline can render it instead of
// showing raw "<p>...</p>" tags as plain text.
const looksLikeHtml = (val) => typeof val === 'string' && /<\/?[a-z][\s\S]*>/i.test(val);

const categorize = (log) => {
  if (log.table_name === 'irm_contact_doctor_map') return 'DOCTOR';
  if (log.action === 'CREATE') return 'CREATED';
  if (log.action === 'DELETE') return 'DELETED';
  if (log.table_name === 'irm_phones') return 'PHONE';
  if (log.table_name === 'irm_addresses') return 'ADDRESS';
  if (log.table_name === 'irm_contacts') return 'PROFILE';
  return 'UPDATED';
};

const tableLabelFor = (log) => {
  // irm_contact_histories holds Menstrual, Surgical and generic Patient History
  // entries together — history_type (resolved server-side from the param slug)
  // is the only way to tell which section an entry actually belongs to.
  return log.history_type
    || TABLE_LABELS[log.table_name]
    || humanizeField(log.table_name?.replace(/^irm_/, ''));
};

// One field-level change within a grouped save (e.g. "History Date" or
// "Remarks" inside one History Updated card).
const buildFieldLine = (log) => {
  const isHistoryTable = HISTORY_TABLES.includes(log.table_name);
  const isDoctorChange = log.table_name === 'irm_contact_doctor_map' && log.field_name === 'irm_users_id';

  const hasDisplayOverride =
    (log.old_value_display !== undefined && log.old_value_display !== null) ||
    (log.new_value_display !== undefined && log.new_value_display !== null);

  const oldDisplay = isDoctorChange
    ? (log.old_value_display || 'Not assigned')
    : hasDisplayOverride
      ? (log.old_value_display ?? safeDisplayValue(log.old_value))
      : safeDisplayValue(log.old_value);
  const newDisplay = isDoctorChange
    ? (log.new_value_display || 'Not assigned')
    : hasDisplayOverride
      ? (log.new_value_display ?? safeDisplayValue(log.new_value))
      : safeDisplayValue(log.new_value);

  const fieldLabel = isDoctorChange
    ? 'Doctor'
    : isHistoryTable
      ? (log.param_member_name || humanizeField(log.field_name))
      : humanizeField(log.field_name);

  // Structured JSON blobs (straw/CAN/CANE target, Lab Condition reading,
  // outcome record, etc.) get their own key-value render instead of being
  // squashed into one "→" text line — parsed off the raw value so a
  // backend-supplied *_display override always wins first. UPDATE diffs
  // key-by-key (only changed keys shown); CREATE/DELETE has only one side
  // to show, so that side's full snapshot is the whole story.
  const canParseJson = !isDoctorChange && !hasDisplayOverride;
  const diffEntries = (canParseJson && log.action === 'UPDATE') ? diffJsonEntries(log.old_value, log.new_value) : null;
  const oldEntries = (canParseJson && log.action !== 'UPDATE') ? parseJsonEntries(log.old_value) : null;
  const newEntries = (canParseJson && log.action !== 'UPDATE') ? parseJsonEntries(log.new_value) : null;

  return {
    id: log.id,
    action: log.action,
    fieldLabel,
    oldDisplay,
    newDisplay,
    isHtml: looksLikeHtml(oldDisplay) || looksLikeHtml(newDisplay),
    isJsonObject: !!(diffEntries || oldEntries || newEntries),
    diffEntries,
    oldEntries,
    newEntries,
  };
};

// Groups the raw per-field audit rows written by one save action (shared
// batch_id, resolved server-side) into a single card — a History save that
// touched group member + date + remarks becomes one entry with three field
// lines instead of three unrelated-looking rows.
const buildGroupEvent = (group) => {
  const entries = group.entries || [];
  const primary = entries[0] || {};
  const category = categorize(primary);

  const distinctLabels = [...new Set(entries.map((e) => tableLabelFor(e)))];
  const actionWord = primary.action === 'CREATE'
    ? 'Added'
    : primary.action === 'DELETE'
      ? 'Removed'
      : 'Updated';
  const titleBase = distinctLabels.length <= 2
    ? distinctLabels.join(' & ')
    : `${distinctLabels.slice(0, 2).join(' & ')} +${distinctLabels.length - 2} more`;
  const title = category === 'DOCTOR' && distinctLabels.length === 1
    ? 'Doctor Changed'
    : `${titleBase} ${actionWord}`;

  return {
    batch_id: group.batch_id,
    category,
    title,
    who: group.updated_by_name || (group.updated_by ? `User #${group.updated_by}` : 'System'),
    at: group.created_at,
    fields: entries.map(buildFieldLine),
  };
};

const richTextSx = {
  fontSize: '0.74rem',
  color: '#444',
  lineHeight: 1.4,
  wordBreak: 'break-word',
  '& p, & div': { margin: 0 },
  '& ul, & ol': { margin: 0, paddingLeft: '18px' },
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Renders a parsed JSON object/array's {label, value} pairs as a compact
// key-value list — used instead of a raw "{...}" blob for structured
// fields like straw/CAN/CANE target details, Lab Condition readings, or
// outcome records.
function JsonEntriesList({ entries }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15 }}>
      {entries.map((e, i) => (
        <Box key={`${e.label}-${i}`} sx={{ display: 'flex', gap: 0.5, fontSize: '0.7rem', lineHeight: 1.5 }}>
          <Typography component="span" sx={{ fontSize: '0.7rem', color: '#777', fontWeight: 600, flexShrink: 0 }}>
            {e.label}:
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.7rem', color: '#333', wordBreak: 'break-word' }}>
            {e.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// Renders a diffJsonEntries() result — one row per key that actually
// changed, as "Key: old → new" (same "before → after" shape as a plain
// field, just repeated per key instead of squashing the whole object).
function JsonDiffList({ entries }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15 }}>
      {entries.map((e, i) => (
        <Box key={`${e.label}-${i}`} sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>
          <Typography component="span" sx={{ fontSize: '0.7rem', color: '#777', fontWeight: 600 }}>
            {e.label}:{' '}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.7rem', color: '#999' }}>
            {e.oldValue}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.7rem', color: '#888', mx: 0.4 }}>→</Typography>
          <Typography component="span" sx={{ fontSize: '0.7rem', color: '#333', fontWeight: 600 }}>
            {e.newValue}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function FieldLine({ field }) {
  if (field.isJsonObject) {
    return (
      <Box sx={{ mt: 0.6 }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#666', fontWeight: 700 }}>
          {field.fieldLabel}
        </Typography>
        {field.action === 'UPDATE' && <JsonDiffList entries={field.diffEntries} />}
        {field.action === 'CREATE' && (
          field.newEntries ? <JsonEntriesList entries={field.newEntries} /> : <Typography sx={{ fontSize: '0.7rem', color: '#333' }}>{field.newDisplay}</Typography>
        )}
        {field.action === 'DELETE' && (
          <Box sx={{ opacity: 0.75 }}>
            {field.oldEntries ? <JsonEntriesList entries={field.oldEntries} /> : <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>{field.oldDisplay}</Typography>}
          </Box>
        )}
      </Box>
    );
  }

  if (field.isHtml) {
    return (
      <Box sx={{ mt: 0.6 }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#666', fontWeight: 700 }}>
          {field.fieldLabel}
        </Typography>
        {field.action === 'UPDATE' && (
          <>
            <Typography sx={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>
              Before
            </Typography>
            <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: field.oldDisplay }} />
            <Typography sx={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', mt: 0.4 }}>
              After
            </Typography>
            <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: field.newDisplay }} />
          </>
        )}
        {field.action === 'CREATE' && (
          <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: field.newDisplay }} />
        )}
        {field.action === 'DELETE' && (
          <Box sx={{ ...richTextSx, textDecoration: 'line-through', opacity: 0.75 }} dangerouslySetInnerHTML={{ __html: field.oldDisplay }} />
        )}
      </Box>
    );
  }

  const description = field.action === 'CREATE'
    ? `Set to: ${field.newDisplay}`
    : field.action === 'DELETE'
      ? `Removed value: ${field.oldDisplay}`
      : `${field.oldDisplay}  →  ${field.newDisplay}`;

  return (
    <Typography sx={{ fontSize: '0.74rem', color: '#444', mt: 0.5, lineHeight: 1.4, wordBreak: 'break-word' }}>
      <Typography component="span" sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#555' }}>
        {field.fieldLabel}:{' '}
      </Typography>
      {description}
    </Typography>
  );
}

function TimelineEvent({ event, isLast }) {
  const cfg = CATEGORY_CONFIG[event.category] || CATEGORY_CONFIG.UPDATED;
  const Icon = cfg.icon;

  return (
    <Box sx={{ display: 'flex', gap: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%', background: cfg.bg,
          border: `2px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, zIndex: 1, boxShadow: `0 0 0 3px ${cfg.dot}22`,
        }}>
          <Icon sx={{ fontSize: 18, color: cfg.icon_color }} />
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, background: `linear-gradient(to bottom, ${cfg.dot}55, ${cfg.dot}11)`, mt: 0.5, mb: 0.5, minHeight: 24 }} />
        )}
      </Box>

      <Box sx={{
        flex: 1, mb: isLast ? 0 : 2,
        p: 1.5, borderRadius: 2,
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: cfg.label_color, lineHeight: 1.3 }}>
          {event.title}
        </Typography>

        <Box sx={{ mt: 0.2 }}>
          {event.fields.map((field) => (
            <FieldLine key={field.id} field={field} />
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8 }}>
          <Typography sx={{ fontSize: '0.66rem', color: '#777', fontWeight: 600 }}>
            By {event.who}
          </Typography>
          <Typography sx={{ fontSize: '0.63rem', color: '#aaa' }}>
            {fmtDateTime(event.at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function AuditTimelineList({ groups, loading, emptyLabel }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} sx={{ color: '#3949ab' }} />
      </Box>
    );
  }

  const events = (groups || []).map(buildGroupEvent);

  if (!events.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: '#bbb' }}>
        <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
        <Typography sx={{ fontSize: '0.85rem' }}>{emptyLabel || 'No changes recorded yet'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0.5, pt: 0.5 }}>
      {events.map((ev, i) => (
        <TimelineEvent key={ev.batch_id} event={ev} isLast={i === events.length - 1} />
      ))}
    </Box>
  );
}
